import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi } from "../api/authApi";

export const AUTH_STATUSES = Object.freeze({
    CHECKING: 'checking',
    ANONYMOUS: 'anonymous',
    REDIRECTING: 'redirecting',
    AUTHENTICATED: 'authenticated',
    LOGGING_OUT: 'logging_out',
    LOGOUT_ERROR: 'logout_error',
    ERROR: 'error'
});

const initialState = {
    status: AUTH_STATUSES.CHECKING,
    user: null,
    error: null,
    bootstrapPending: false
};

const getAuthError = (error) => {
    if (error?.status === 403) {
        return { type: 'forbidden', status: 403 };
    }
    if (error?.status === 'FETCH_ERROR' || error?.status === 'TIMEOUT_ERROR') {
        return { type: 'network', status: null };
    }
    if (typeof error?.status === 'number' && error.status >= 500) {
        return { type: 'server', status: error.status };
    }
    return {
        type: 'unexpected',
        status: typeof error?.status === 'number' ? error.status : null
    };
};

export const bootstrapAuth = createAsyncThunk(
    'auth/bootstrap',
    async (_, { dispatch, rejectWithValue }) => {
        const request = dispatch(authApi.endpoints.getAuthMe.initiate(undefined, {
            forceRefetch: true
        }));

        try {
            return await request.unwrap();
        } catch (error) {
            return rejectWithValue(error);
        } finally {
            request.unsubscribe();
        }
    },
    {
        condition: (_, { getState }) => !getState().auth.bootstrapPending
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        requestAuthCheck: (state) => {
            state.status = AUTH_STATUSES.CHECKING;
            state.user = null;
            state.error = null;
        },
        setAuthenticated: (state, action) => {
            // Transitional callback/logout compatibility. A successful callback
            // still has to be verified by the session-backed /api/auth/me endpoint.
            state.status = action.payload
                ? AUTH_STATUSES.CHECKING
                : AUTH_STATUSES.ANONYMOUS;
            state.user = null;
            state.error = null;
        },
        setIsAuthenticating: (state, action) => {
            if (action.payload) {
                state.status = AUTH_STATUSES.REDIRECTING;
                state.user = null;
                state.error = null;
            } else if (state.status === AUTH_STATUSES.REDIRECTING) {
                state.status = AUTH_STATUSES.ANONYMOUS;
            }
        },
        logoutStarted: (state) => {
            state.status = AUTH_STATUSES.LOGGING_OUT;
            state.user = null;
            state.error = null;
            state.bootstrapPending = false;
        },
        logoutSucceeded: (state) => {
            state.status = AUTH_STATUSES.ANONYMOUS;
            state.user = null;
            state.error = null;
            state.bootstrapPending = false;
        },
        logoutFailed: (state, action) => {
            state.status = AUTH_STATUSES.LOGOUT_ERROR;
            state.user = null;
            state.error = action.payload;
            state.bootstrapPending = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(bootstrapAuth.pending, (state) => {
                state.status = AUTH_STATUSES.CHECKING;
                state.user = null;
                state.error = null;
                state.bootstrapPending = true;
            })
            .addCase(bootstrapAuth.fulfilled, (state, action) => {
                state.status = AUTH_STATUSES.AUTHENTICATED;
                state.user = action.payload;
                state.error = null;
                state.bootstrapPending = false;
            })
            .addCase(bootstrapAuth.rejected, (state, action) => {
                state.user = null;
                state.bootstrapPending = false;

                if (action.payload?.status === 401) {
                    state.status = AUTH_STATUSES.ANONYMOUS;
                    state.error = null;
                    return;
                }

                state.status = AUTH_STATUSES.ERROR;
                state.error = getAuthError(action.payload || action.error);
            });
    }
})

export const authStatusSelector = state => state.auth.status;
export const authenticatedUserSelector = state => state.auth.user;
export const authErrorSelector = state => state.auth.error;
export const authBootstrapPendingSelector = state => state.auth.bootstrapPending;
export const authenticationSelector = state => (
    state.auth.status === AUTH_STATUSES.AUTHENTICATED && Boolean(state.auth.user)
);
export const authInProgressSelector = state => [
    AUTH_STATUSES.CHECKING,
    AUTH_STATUSES.REDIRECTING,
    AUTH_STATUSES.LOGGING_OUT
].includes(state.auth.status);
export const logoutErrorSelector = state => (
    state.auth.status === AUTH_STATUSES.LOGOUT_ERROR ? state.auth.error : null
);

export const {
    requestAuthCheck,
    setAuthenticated,
    setIsAuthenticating,
    logoutStarted,
    logoutSucceeded,
    logoutFailed
} = authSlice.actions;

export default authSlice.reducer;
